import { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CONSENT_TEXT } from '../consent';
import SignaturePad, { SignatureChoice, SignatureStyle } from './SignaturePad';

interface SignatureConsentProps {
  document: {
    id: string;
    title: string;
    created_at: string;
  };
  signer: {
    full_name: string;
    email: string;
    role?: string;
  };
  signatureStyle: SignatureStyle;
  signatureField: { page: number; source: string } | null;
  signatureZones?: Array<{ page: number; source: string }>;
  onConsent: (signature: SignatureChoice | null) => void;
  onDecline: () => void;
}

function SignatureConsent({ document, signer, signatureStyle, signatureField, signatureZones = [], onConsent, onDecline }: SignatureConsentProps) {
  const [consentChecked, setConsentChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [choice, setChoice] = useState<SignatureChoice | null>(null);
  const needsSignature = signatureStyle !== 'none';
  const ready = consentChecked && (!needsSignature || choice !== null);

  const handleSign = async () => {
    if (!ready) return;
    setLoading(true);
    await onConsent(needsSignature ? choice : null);
  };

  return (
    <div className="card">
      <h2 className="text-2xl font-bold mb-6">Consentement de signature</h2>

      <div className="space-y-6">
        {/* Document Info */}
        <div className="bg-gray-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">Document :</span>
            <span className="text-sm text-gray-900">{document.title}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">Signataire :</span>
            <span className="text-sm text-gray-900">{signer.full_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">Email :</span>
            <span className="text-sm text-gray-900">{signer.email}</span>
          </div>
          {signer.role && (
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-700">Rôle :</span>
              <span className="text-sm text-gray-900">{signer.role}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-700">Date :</span>
            <span className="text-sm text-gray-900">
              {format(new Date(), 'PPPp', { locale: fr })}
            </span>
          </div>
        </div>

        {/* Legal Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
          <h3 className="font-semibold text-blue-900">
            À propos de votre signature électronique
          </h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• Il s'agit d'une <strong>signature électronique</strong> au sens du règlement eIDAS : elle ne peut être écartée comme preuve au seul motif qu'elle est électronique (art. 25).</li>
            <li>• Sa force probante est appréciée par le juge (art. 1366 et 1367 du Code civil), au vu du dossier de preuve qui vous sera remis.</li>
            <li>• Un certificat éphémère unique sera généré pour cette signature : toute modification du document après signature sera détectable.</li>
            <li>• La signature sera horodatée par un tiers de confiance (RFC 3161).</li>
            <li>• Un dossier de preuve complet sera généré et archivé.</li>
          </ul>
        </div>

        {/* Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-900 mb-2">⚠️ Important</h4>
          <p className="text-sm text-amber-800">
            En signant ce document, vous manifestez votre <strong>consentement éclairé</strong>.
            Assurez-vous d'avoir lu et compris l'intégralité du document avant de signer.
          </p>
        </div>

        {/* Apparence de la signature */}
        {needsSignature && (
          <div className="space-y-2">
            <SignaturePad style={signatureStyle as Exclude<SignatureStyle, 'none'>} signerName={signer.full_name} onChange={setChoice} />
            {signatureZones.length > 1 && (
              <p className="text-xs text-gray-600">
                Vous signez une seule fois : votre signature sera reproduite à {signatureZones.length} emplacements du document
                (pages {signatureZones.map((zone) => zone.page).join(', ')}).
              </p>
            )}
            {signatureZones.length <= 1 && signatureField && (
              <p className="text-xs text-gray-600">
                Votre signature apparaîtra {signatureField.source === 'signature_page'
                  ? `sur la page de signatures ajoutée à la fin du document (page ${signatureField.page})`
                  : `à la page ${signatureField.page} du document`}.
              </p>
            )}
          </div>
        )}

        {/* Consent Checkbox */}
        <div className="border-2 border-gray-300 rounded-lg p-4">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={consentChecked}
              onChange={(e) => setConsentChecked(e.target.checked)}
              className="mt-1 h-5 w-5 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className="ml-3 text-sm text-gray-700">{CONSENT_TEXT}</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            onClick={handleSign}
            disabled={!ready || loading}
            className="btn btn-primary flex-1 text-lg py-3"
          >
            {loading ? (
              <>
                <span className="inline-block animate-spin mr-2">⏳</span>
                Signature en cours...
              </>
            ) : (
              <>
                ✍️ Signer le document
              </>
            )}
          </button>
        </div>

        <button
          onClick={onDecline}
          disabled={loading}
          className="btn btn-secondary w-full"
        >
          Refuser de signer
        </button>

        {/* Legal Footer */}
        <div className="pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Signature électronique au sens du règlement eIDAS (UE) n° 910/2014, non qualifiée.
            Pour toute question, contactez l'émetteur du document.
          </p>
        </div>
      </div>
    </div>
  );
}

export default SignatureConsent;
